/**
 * Draggable Tutorial Tooltip mit Minimize, Keyboard Shortcuts, Undo
 * Komplett verschiebbar wie Windows-Fenster
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, CheckCircle2, Minimize2, Maximize2, Undo2, Volume2, VolumeX, GripHorizontal, Keyboard } from 'lucide-react';
import { Button } from '../ui/button';
import { useTutorial } from '@/app/contexts/TutorialContext';
import type { TutorialStep } from '@/app/types/tutorial';

interface TutorialTooltipDraggableProps {
  step: TutorialStep;
  targetRect: DOMRect | null;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onClose: () => void;
  conditionMet?: boolean;
}

export function TutorialTooltipDraggable({
  step,
  targetRect,
  onNext,
  onPrevious,
  onSkip,
  onClose,
  conditionMet = false,
}: TutorialTooltipDraggableProps) {
  const { undoTutorialStep, toggleSound, state } = useTutorial();
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Initial position berechnen
  useEffect(() => {
    if (position === null) {
      const initialPos = getInitialPosition();
      setPosition(initialPos);
    }
  }, [targetRect, position]);

  const getInitialPosition = () => {
    const tooltipWidth = 450;
    const tooltipHeight = 300;
    const gap = 20;
    const viewportPadding = 20; // Mindestabstand zum Viewport-Rand

    if (!targetRect) {
      // Zentriert im sichtbaren Viewport
      return {
        x: Math.max(viewportPadding, (window.innerWidth - tooltipWidth) / 2),
        y: Math.max(viewportPadding, (window.innerHeight - tooltipHeight) / 2),
      };
    }

    let x = 0;
    let y = 0;

    switch (step.tooltipPosition) {
      case 'top':
        x = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        y = targetRect.top - tooltipHeight - gap;
        break;

      case 'bottom':
        x = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        y = targetRect.bottom + gap;
        break;

      case 'left':
        x = targetRect.left - tooltipWidth - gap;
        y = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        break;

      case 'right':
        x = targetRect.right + gap;
        y = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        break;

      case 'center':
      default:
        x = (window.innerWidth - tooltipWidth) / 2;
        y = (window.innerHeight - tooltipHeight) / 2;
        break;
    }

    // WICHTIG: Ensure within viewport with proper padding
    // Wenn Dialog außerhalb ist, verschiebe ihn in den sichtbaren Bereich
    x = Math.max(viewportPadding, Math.min(x, window.innerWidth - tooltipWidth - viewportPadding));
    y = Math.max(viewportPadding, Math.min(y, window.innerHeight - tooltipHeight - viewportPadding));

    // Extra-Check: Wenn targetRect außerhalb ist, ignoriere es und zentriere
    if (
      targetRect.top < 0 ||
      targetRect.left < 0 ||
      targetRect.bottom > window.innerHeight ||
      targetRect.right > window.innerWidth
    ) {
      console.warn('[Tutorial] Target element außerhalb Viewport, zentriere Dialog');
      return {
        x: Math.max(viewportPadding, (window.innerWidth - tooltipWidth) / 2),
        y: Math.max(viewportPadding, (window.innerHeight - tooltipHeight) / 2),
      };
    }

    return { x, y };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (position !== null) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, position]);

  if (!position) return null;

  return (
    <div
      ref={tooltipRef}
      className="pointer-events-auto rounded-lg shadow-2xl overflow-hidden transition-all duration-200"
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: isMinimized ? '300px' : '450px',
        maxWidth: '90vw',
        backgroundColor: 'var(--md-surface)',
        color: 'var(--md-on-surface)',
        border: '2px solid var(--md-primary)',
        zIndex: 10002,
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      }}
    >
      {/* Draggable Header */}
      <div
        className="px-4 py-3 border-b cursor-move select-none"
        style={{
          backgroundColor: 'var(--md-primary)',
          color: 'var(--md-on-primary)',
          borderColor: 'var(--md-outline-variant)',
        }}
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <GripHorizontal className="w-5 h-5 flex-shrink-0 opacity-70" />
            <h3 className="text-base font-semibold truncate">{step.title}</h3>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Sound Toggle */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSound();
              }}
              className="p-1.5 rounded hover:bg-white/20 transition-colors"
              title={state.soundEnabled ? 'Sound deaktivieren' : 'Sound aktivieren'}
            >
              {state.soundEnabled ? (
                <Volume2 className="w-4 h-4" />
              ) : (
                <VolumeX className="w-4 h-4" />
              )}
            </button>
            
            {/* Minimize */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMinimized(!isMinimized);
              }}
              className="p-1.5 rounded hover:bg-white/20 transition-colors"
              title={isMinimized ? 'Maximieren' : 'Minimieren'}
            >
              {isMinimized ? (
                <Maximize2 className="w-4 h-4" />
              ) : (
                <Minimize2 className="w-4 h-4" />
              )}
            </button>
            
            {/* Close */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="p-1.5 rounded hover:bg-white/20 transition-colors"
              title="Schließen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Content - nur wenn nicht minimiert */}
      {!isMinimized && (
        <>
          <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
            <p className="text-sm leading-relaxed whitespace-pre-line mb-3">
              {step.description}
            </p>

            {/* Keyboard Shortcut Hint */}
            {step.keyboardShortcut && (
              <div
                className="mt-3 px-3 py-2 rounded flex items-center gap-2 text-sm"
                style={{
                  backgroundColor: 'var(--md-tertiary-container)',
                  color: 'var(--md-on-tertiary-container)',
                }}
              >
                <Keyboard className="w-4 h-4 flex-shrink-0" />
                <div className="flex items-center gap-2 flex-wrap">
                  <kbd
                    className="px-2 py-1 rounded text-xs font-mono font-semibold"
                    style={{
                      backgroundColor: 'var(--md-surface)',
                      border: '1px solid var(--md-outline)',
                    }}
                  >
                    {step.keyboardShortcut}
                  </kbd>
                  {step.keyboardHint && (
                    <span className="text-xs">{step.keyboardHint}</span>
                  )}
                </div>
              </div>
            )}

            {/* Condition Met Indicator */}
            {conditionMet && (
              <div
                className="mt-3 px-3 py-2 rounded text-sm flex items-center gap-2 animate-pulse"
                style={{
                  backgroundColor: 'var(--md-primary-container)',
                  color: 'var(--md-on-primary-container)',
                }}
              >
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="font-semibold">Perfekt! Aktion erkannt ✓</span>
              </div>
            )}

            {/* Action hints */}
            {step.action === 'click' && !conditionMet && (
              <div
                className="mt-3 px-3 py-2 rounded text-sm flex items-center gap-2"
                style={{
                  backgroundColor: 'var(--md-primary-container)',
                  color: 'var(--md-on-primary-container)',
                }}
              >
                <span className="text-xl">👆</span>
                <span>Klicken Sie auf das hervorgehobene Element, dann auf "Weiter"</span>
              </div>
            )}

            {step.action === 'wait' && !conditionMet && (
              <div
                className="mt-3 px-3 py-2 rounded text-sm flex items-center gap-2"
                style={{
                  backgroundColor: 'var(--md-secondary-container)',
                  color: 'var(--md-on-secondary-container)',
                }}
              >
                <span className="text-xl">✋</span>
                <span>
                  Führen Sie die Aktion aus, dann klicken Sie auf "Weiter"
                </span>
              </div>
            )}

            {step.action === 'input' && !conditionMet && (
              <div
                className="mt-3 px-3 py-2 rounded text-sm flex items-center gap-2"
                style={{
                  backgroundColor: 'var(--md-tertiary-container)',
                  color: 'var(--md-on-tertiary-container)',
                }}
              >
                <span className="text-xl">⌨️</span>
                <span>Geben Sie die Daten ein, dann klicken Sie auf "Weiter"</span>
              </div>
            )}

            {step.action === 'drag-drop' && !conditionMet && (
              <div
                className="mt-3 px-3 py-2 rounded text-sm flex items-center gap-2"
                style={{
                  backgroundColor: 'var(--md-primary-container)',
                  color: 'var(--md-on-primary-container)',
                }}
              >
                <span className="text-xl">🖱️</span>
                <span>Ziehen Sie das Element per Drag & Drop, dann "Weiter"</span>
              </div>
            )}

            {step.action === 'auto' && !conditionMet && (
              <div
                className="mt-3 px-3 py-2 rounded text-sm flex items-center gap-2"
                style={{
                  backgroundColor: 'var(--md-tertiary-container)',
                  color: 'var(--md-on-tertiary-container)',
                }}
              >
                <span className="text-xl">📖</span>
                <span>Klicken Sie auf "Weiter" um fortzufahren</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className="px-4 py-3 border-t flex items-center justify-between gap-2"
            style={{ borderColor: 'var(--md-outline-variant)' }}
          >
            <div className="flex items-center gap-2">
              <Button
                onClick={onPrevious}
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={false}
              >
                <ChevronLeft className="w-4 h-4" />
                Zurück
              </Button>

              {/* Undo Button */}
              <Button
                onClick={undoTutorialStep}
                variant="outline"
                size="sm"
                className="gap-1.5"
                title="Schritt rückgängig machen"
              >
                <Undo2 className="w-4 h-4" />
              </Button>
            </div>

            {/* Always show Next button - user must click it */}
            <Button
              onClick={onNext}
              size="sm"
              className="gap-1.5"
              style={{
                backgroundColor: 'var(--md-primary)',
                color: 'var(--md-on-primary)',
              }}
            >
              Weiter
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </>
      )}

      {/* Minimized State */}
      {isMinimized && (
        <div className="px-4 py-2 text-sm text-center opacity-70">
          Tutorial läuft...
        </div>
      )}
    </div>
  );
}